import React, { useState, useContext } from 'react'
import styles from './index.module.css'
import { FormContext } from '../context'
import type { Rule } from '../Validations'
import Typography from '../../Typography'
import useInputValue from '../hooks/useInputValue'

interface IRadioButtonProps {
  name: string
  value: string | boolean | number
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  validations?: Array<ReturnType<Rule>>
  hover?: boolean
  active?: boolean
  focus?: boolean
  error?: boolean
  onClick?: () => void
}

const RadioButton = ({
  name,
  value,
  defaultChecked,
  onChange,
  disabled,
  validations,
  hover = false,
  active = false,
  focus = false,
  error: errorProp,
  onClick,
  ...props
}: IRadioButtonProps) => {
  const [checked, setChecked] = useState(defaultChecked)

  const { errors } = useContext(FormContext)

  const { onInputChange } = useInputValue({
    name,
    defaultValue: defaultChecked ? value : undefined,
    multiple: false,
    trueValue: value,
    falseValue: '',
    validations,
    setInputValue: formValue => {
      const isChecked = formValue === value
      setChecked(isChecked)
    },
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onInputChange(value)
      setChecked(true)
      onChange?.(true)
      onClick?.()
    }
  }

  const hasValidations = Array.isArray(validations) && validations.length > 0
  const hasError = errorProp || !!errors[name]

  const state = [
    hover && 'hover',
    active && 'active',
    focus && 'focus',
    disabled && 'disabled',
    hasError && 'error',
    checked && 'checked',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <input
        name={name}
        type="radio"
        value={String(value)}
        checked={checked ?? false}
        disabled={disabled}
        onChange={handleChange}
        className={styles.radioButton}
        data-radio-state={state || undefined}
        data-state-validations={hasValidations}
        data-state-error={hasError}
        aria-checked={checked}
        aria-disabled={disabled}
        {...props}
      />
      {hasError && errors[name] && (
        <Typography variant="caption" size="l" color="var(--color-error-500)" data-error-message>
          {errors[name]}
        </Typography>
      )}
    </>
  )
}

RadioButton.displayName = 'Form.RadioButton'
export default RadioButton
